import React from "react";
import { motion } from "framer-motion";
import {
    Globe2,
    DollarSign,
    Sparkles,
    Users2,
    BarChart3,
    ArrowUpRight,
} from "lucide-react";

const FEATURES = [
    {
        icon: Globe2,
        title: "Global distribution",
        desc: "Deliver to 150+ stores and 50+ countries from a single dashboard. Release-day everywhere.",
        span: "lg:col-span-7",
        big: true,
    },
    {
        icon: DollarSign,
        title: "Artist monetization",
        desc: "Royalty splits, sync licensing, fan tipping.",
        span: "lg:col-span-5",
    },
    {
        icon: Sparkles,
        title: "Content creation",
        desc: "AI-assisted mastering, cover art generation and short-form clip cutter.",
        span: "lg:col-span-4",
    },
    {
        icon: Users2,
        title: "Collaboration tools",
        desc: "Version-controlled stems, real-time chat with collaborators, transparent splits.",
        span: "lg:col-span-4",
    },
    {
        icon: BarChart3,
        title: "Analytics dashboard",
        desc: "Stream sources, listener geography, conversion funnels — all in real time.",
        span: "lg:col-span-4",
    },
];

export const Features = () => {
    return (
        <section
            id="features"
            className="relative py-28 lg:py-36 grain"
            data-testid="features-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 relative">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 lg:mb-20"
                >
                    <div className="max-w-2xl">
                        <p className="text-sm uppercase tracking-[0.3em] text-[#F54815] mb-4 font-medium">
                            Features
                        </p>
                        <h2
                            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
                            data-testid="features-heading"
                        >
                            Every tool an artist
                            <br />
                            actually <span className="text-[#791EE2]">needs.</span>
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-md font-body">
                        From upload to payout — purpose-built infrastructure
                        for the modern recording artist.
                    </p>
                </motion.div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
                    {FEATURES.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            className={`group relative ${f.span} p-8 lg:p-10 rounded-3xl border border-white/10 bg-white/[0.03] hover:border-[#791EE2]/40 hover:bg-white/[0.06] transition-all duration-500 overflow-hidden`}
                            data-testid={`feature-${i}`}
                        >
                            <div className="flex items-start justify-between mb-8">
                                <div className="w-12 h-12 rounded-xl bg-[#791EE2]/15 text-[#791EE2] flex items-center justify-center group-hover:bg-[#791EE2] group-hover:text-white transition-colors">
                                    <f.icon size={22} strokeWidth={1.8} />
                                </div>
                                <ArrowUpRight
                                    size={20}
                                    className="text-white/30 group-hover:text-white group-hover:rotate-12 transition-all"
                                />
                            </div>
                            <h3
                                className={`font-heading font-bold text-white tracking-tight mb-3 ${
                                    f.big
                                        ? "text-3xl lg:text-4xl"
                                        : "text-xl lg:text-2xl"
                                }`}
                            >
                                {f.title}
                            </h3>
                            <p className="text-white/55 font-body leading-relaxed max-w-lg">
                                {f.desc}
                            </p>

                            {f.big && (
                                <div className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-[#791EE2] opacity-15 blur-[100px] pointer-events-none" />
                            )}
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
